'use client'
import styled from '@emotion/styled'

import { Input } from '@common/components/Input'
import { TextArea } from '@common/components/TextArea'
import { EmailOptions, PhoneNumberOptions } from '@common/utils/forms'
import { ButtonContainer } from '@common/components/ButtonContainer'
import { Button } from '@common/components/Button'
import toast from 'react-hot-toast'
import { Dropdown } from '@common/components/Dropdown'
import { useContactForm } from './use-contact-form'
import { SiteContainer } from '@app/site/common/components/SiteContainer'
import Head from 'next/head'
import { useSlug } from '@common/hooks/use-slug'
import { useSlimStaticWebsite } from '@app/site/common/hooks/use-slim-static-website'
import Navigate from '@common/utils/Navigate'
import { useRouter } from 'next/navigation'

export default function ClientPage() {
  const router = useRouter()
  const slug = useSlug()
  const { name, loading: loadingPage } = useSlimStaticWebsite(slug)

  const { errors, onSubmit, register, isValid, categories, loading, hasErrandsFeature, errandInfo } = useContactForm(
    () => {
      toast.success('Meddelandet har skickats')
      router.replace('/')
    },
  )

  if (loading || loadingPage) {
    return null
  }

  if (hasErrandsFeature === false) {
    return <Navigate to="/" />
  }

  return (
    <>
      <Head>
        <meta charSet="utf-8" />
        <title>Kontakta oss | {name}</title>
      </Head>
      <SiteContainer small={true} style={{ paddingBottom: '10rem' }}>
        <InnerContainer>
          <HeadlineSection>
            <Headline>Kontakta oss</Headline>
          </HeadlineSection>
          {errandInfo && <InfoText>{errandInfo}</InfoText>}
          <form style={{ paddingBottom: '1rem' }} onSubmit={onSubmit}>
            <TextArea
              skeleton={loading}
              autoFocus={true}
              required={true}
              error={errors.body?.message}
              style={{ maxWidth: '100%', minWidth: '100%' }}
              label="Meddelande"
              {...register('body', { required: 'Meddelandet kan inte vara tomt' })}
            />
            <div style={{ marginTop: '1rem' }} />
            <Input
              skeleton={loading}
              required={true}
              error={errors.email?.message}
              label="E-mail"
              placeholder="exempel@mail.com"
              {...register('email', EmailOptions)}
            />
            <div style={{ marginTop: '1rem' }} />
            <Input
              skeleton={loading}
              required={true}
              error={errors.phoneNumber?.message}
              label="Telefonnummer"
              placeholder="+46701234567"
              {...register('phoneNumber', PhoneNumberOptions)}
            />
            <div style={{ marginTop: '1rem' }} />
            <Input
              skeleton={loading}
              required={false}
              error={errors.apartmentNumber?.message}
              label="Lägenhetsnummer"
              placeholder="LGH 1928 (valfritt)"
              {...register('apartmentNumber', { required: false })}
            />
            <div style={{ marginTop: '1rem' }} />
            <Dropdown
              options={categories}
              skeleton={loading}
              required={true}
              error={errors.errandTypeId?.message}
              label="Kategori"
              {...register('errandTypeId', { required: 'Ange en kategori' })}
            />
            <div style={{ marginTop: '2rem' }} />
            <ButtonContainer>
              <Button type="submit" disabled={!isValid}>
                Skicka
              </Button>
            </ButtonContainer>
          </form>
        </InnerContainer>
      </SiteContainer>
    </>
  )
}

const InfoText = styled.div`
  margin-bottom: 2rem;

  white-space: pre-wrap;
`

const InnerContainer = styled.div`
  width: 100%;
  max-width: 50rem;
  min-width: 100%;

  @media (max-width: 800px) {
    margin-top: 3rem;
  }
`
const HeadlineSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  border-bottom: 1px solid gainsboro;
  width: 100%;
  margin-bottom: 1rem;
`

const Headline = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  padding-bottom: 0.5rem;
`
